import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, Navigate } from "react-router-dom";
import { api, RSD } from "../../lib/api";
import { Search, ChevronDown, ChevronUp } from "lucide-react";
import { SourceBadge } from "../../components/SourceBadge";
import { OrderTimeline } from "../../components/OrderTimeline";
import { OrderItemsDetail } from "../../components/OrderItemsDetail";

const RANGES = [[1, "Danas"], [2, "Juče+"], [7, "7 dana"], [30, "30 dana"]];

// Canonical cancellation reason codes → Serbian labels (presentation only).
const CANCEL_LABELS = {
  CUSTOMER_REQUESTED_CANCELLATION: "Kupac otkazao telefonom",
  CUSTOMER_CHANGED_MIND: "Kupac odustao",
  ORDER_ENTRY_ERROR: "Greška pri unosu porudžbine",
  OTHER: "Drugo",
};

function HistoryCard({ o }) {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(null);
  const cancelled = o.status === "cancelled";
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && events === null) {
      try { const { data } = await api.get(`/orders/${o.id}/events`); setEvents(data || []); }
      catch { setEvents([]); }
    }
  };
  return (
    <div data-testid={`hist-order-${o.order_number}`} className="bg-white rounded-xl border p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="font-display font-black">{o.public_code || o.order_number}</span>
          <SourceBadge o={o} />
          {cancelled && (
            <span data-testid={`hist-cancelled-${o.order_number}`}
              className="text-[10px] font-black uppercase tracking-wider rounded-full px-2 py-0.5 bg-brand-red/10 text-brand-red">
              Otkazano
            </span>
          )}
        </div>
        <span className="font-display font-black text-brand-red">{RSD(o.total)}</span>
      </div>
      <p className="text-sm text-slate-600">{o.customer_name}</p>
      <p className="text-xs text-slate-500 mt-1">{o.items.map((it) => `${it.quantity}× ${it.name}`).join(", ")}</p>
      <p className="text-[11px] text-slate-400 mt-1">Preuzimanje: {o.pickup_time}{o.pickup_type === "asap" ? " (ASAP)" : ""} · {new Date(o.created_at).toLocaleString("sr-RS")}</p>
      {o.payment_method && (
        <p data-testid={`hist-payment-${o.order_number}`} className="text-[11px] font-bold text-slate-500 mt-0.5">
          Način plaćanja: {o.payment_method === "card" ? "Kartica" : "Gotovina"}
        </p>
      )}
      <button data-testid={`hist-toggle-${o.order_number}`} onClick={toggle}
        className="mt-2 text-xs font-semibold text-brand-red flex items-center gap-1">
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}{open ? "Sakrij detalje" : "Prikaži celu porudžbinu"}
      </button>
      {open && (
        <div className="mt-3 border-t pt-3 space-y-3">
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Porudžbina</p>
            <OrderItemsDetail items={o.items} testid={`hist-items-${o.order_number}`} />
          </div>
          {o.note && (
            <p data-testid={`hist-note-${o.order_number}`} className="text-xs font-bold text-brand-orange">
              Napomena porudžbine: {o.note}
            </p>
          )}
          <p className="text-[11px] font-semibold text-slate-500">
            Status: {o.status === "completed" ? "Preuzeto" : cancelled ? "Otkazano" : o.status} · Ukupno: {RSD(o.total)}
          </p>
          {cancelled && (
            <p data-testid={`hist-cancel-reason-${o.order_number}`} className="text-[11px] font-semibold text-brand-red">
              Razlog otkazivanja: {CANCEL_LABELS[o.cancel_reason] || o.cancel_reason || "—"}
              {o.cancel_note ? ` · ${o.cancel_note}` : ""} · Evidentirao restoran
            </p>
          )}
          <div>
            <p className="text-xs font-black uppercase tracking-wider text-slate-400 mb-1">Tok porudžbine</p>
            {events === null ? <p className="text-sm text-slate-400">Učitavanje…</p>
              : <OrderTimeline events={events} staff={true} />}
          </div>
        </div>
      )}
    </div>
  );
}

export default function OrderHistory() {
  const { restaurant, role, reload } = useOutletContext();
  const rid = restaurant.id;
  // The order archive is financial/terminal data: STAFF is redirected before anything is fetched.
  const canViewArchive = ["OWNER", "MANAGER", "PLATFORM_ADMIN"].includes((role || "").toUpperCase());
  const [days, setDays] = useState(7);
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [src, setSrc] = useState("all");
  useEffect(() => {
    if (!canViewArchive) return;
    api.get(`/restaurant/order-history?restaurant_id=${rid}&days=${days}`)
      .then(({ data }) => setOrders(data))
      .catch((e) => {
        setOrders([]);                       // never keep stale archive rows on screen
        if (e?.response?.status === 403) reload?.();   // role was downgraded → re-read canonical role
      });
  }, [rid, days, canViewArchive, reload]);

  const ql = q.trim().toLowerCase();
  const list = useMemo(() => orders.filter((o) => (!ql ||
    String(o.order_number).includes(ql) || (o.customer_name || "").toLowerCase().includes(ql))
    && (src === "all" || (src === "internal" ? o.order_source === "RESTAURANT_INTERNAL" : o.order_source !== "RESTAURANT_INTERNAL"))), [orders, ql, src]);

  if (!canViewArchive) return <Navigate to={`/restaurant/${rid}/orders`} replace />;

  return (
    <div className="max-w-3xl" data-testid="order-history">
      <h1 className="font-display font-black text-2xl mb-4">Istorija porudžbina</h1>      <div className="flex flex-wrap gap-2 mb-3">
        {RANGES.map(([d, label]) => (
          <button key={d} data-testid={`hist-range-${d}`} onClick={() => setDays(d)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${days === d ? "bg-brand-red hover:bg-brand-redHover text-white" : "bg-slate-100"}`}>{label}</button>
        ))}
      </div>
      <div className="flex gap-2 mb-3">
        {[["all", "Sve"], ["online", "Online"], ["internal", "Lokal"]].map(([v, label]) => (
          <button key={v} data-testid={`hist-src-${v}`} onClick={() => setSrc(v)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold ${src === v ? "bg-slate-800 text-white" : "bg-slate-100"}`}>{label}</button>
        ))}
      </div>
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="hist-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Broj ili ime"
          className="w-full pl-9 pr-3 py-2.5 rounded-lg border-2 border-slate-200 outline-none focus:ring-2 focus:ring-brand-red" />
      </div>
      {list.length === 0 && <p className="text-slate-400 text-sm py-8 text-center">Nema porudžbina u izabranom periodu.</p>}
      <div className="space-y-2">
        {list.map((o) => <HistoryCard key={o.id} o={o} />)}
      </div>
    </div>
  );
}
